import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import { ResultValue } from '@@/types/ApiResult';
import { DfcApiClient } from '../api/dfc-api-client';
import type { EntityRecord, QueryByPathRequest } from '../api/api-entities';
import { CreateEntityPayload, UpdateEntityPayload } from '../schemas/entity';
import { itemsQueryKeys } from './use-items';

const ENTITIES_QUERY_KEY = 'dfc-entities';

export const entitiesQueryKeys = {
  base: (params: { instanceId: string; region: string }) =>
    [ENTITIES_QUERY_KEY, params.instanceId, params.region] as const,

  list: (params: { instanceId: string; region: string; parentId: string }) =>
    [...entitiesQueryKeys.base(params), 'list', params.parentId] as const,

  detail: (params: { instanceId: string; region: string; entityId: string }) =>
    [...entitiesQueryKeys.base(params), 'detail', params.entityId] as const
};

export const useListEntities = ({ parentId }: { parentId: string }) => {
  const { instanceData } = useContext(AuthContext);

  const fetchEntities = async (): Promise<EntityRecord[]> => {
    if (!instanceData?.instanceId) {
      throw new Error('Missing required context data');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.entities.listEntities({
      input: {},
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region,
        parentId: parentId
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch entities');
    }

    return result.data.entities;
  };

  return useQuery({
    queryKey: entitiesQueryKeys.list({
      instanceId: instanceData?.instanceId ?? '',
      region: instanceData?.region ?? '',
      parentId
    }),
    queryFn: fetchEntities,
    staleTime: 4000
  });
};

export const useGetEntity = (entityId?: string, options?: { enabled?: boolean }) => {
  const { instanceData } = useContext(AuthContext);

  const fetchEntity = async (): Promise<EntityRecord> => {
    if (!instanceData?.instanceId || !entityId) {
      throw new Error('Missing required context data or entity ID');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.entities.getEntity({
      input: { entityId },
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch entity');
    }

    return result.data.entity;
  };

  return useQuery({
    queryKey: entitiesQueryKeys.detail({
      instanceId: instanceData?.instanceId ?? '',
      region: instanceData?.region ?? '',
      entityId: entityId ?? ''
    }),
    queryFn: fetchEntity,
    staleTime: 4000,
    enabled: !!(instanceData?.instanceId && entityId && options?.enabled !== false)
  });
};

export const useCreateEntity = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEntityPayload): Promise<EntityRecord> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.entities.createEntity({
        input: data,
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message);
      }

      return result.data.entity;
    },
    onSuccess: () => {
      if (!instanceData?.instanceId || !instanceData?.region) return;
      queryClient.invalidateQueries({
        queryKey: entitiesQueryKeys.base({
          instanceId: instanceData.instanceId,
          region: instanceData.region
        })
      });
    }
  });
};

export const useUpdateEntity = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: UpdateEntityPayload & {
        entityId: string;
      }
    ): Promise<void> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.entities.updateEntity(data.entityId, {
        input: data,
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message);
      }
    },
    onSuccess: (_, variables) => {
      if (!instanceData?.instanceId || !instanceData?.region) return;

      queryClient.invalidateQueries({
        queryKey: entitiesQueryKeys.base({
          instanceId: instanceData.instanceId,
          region: instanceData.region
        })
      });

      queryClient.invalidateQueries({
        queryKey: itemsQueryKeys.base({
          instanceId: instanceData.instanceId,
          region: instanceData.region
        })
      });

      queryClient.invalidateQueries({
        queryKey: entitiesQueryKeys.detail({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId: variables.entityId
        })
      });
    }
  });
};

export const useDeleteEntity = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entityId: string): Promise<void> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.entities.deleteEntity({
        input: { entityId },
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message || 'Failed to delete entity');
      }
    },
    onSuccess: (_, entityId) => {
      if (!instanceData?.instanceId || !instanceData?.region) return;

      queryClient.invalidateQueries({
        queryKey: entitiesQueryKeys.base({
          instanceId: instanceData.instanceId,
          region: instanceData.region
        })
      });

      queryClient.removeQueries({
        queryKey: entitiesQueryKeys.detail({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId
        })
      });
    }
  });
};

export const useQueryByPath = () => {
  const { instanceData } = useContext(AuthContext);

  return useMutation({
    mutationFn: async (data: QueryByPathRequest) => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.entities.queryByPath({
        input: {
          path: data.path
        },
        queryString: {
          region: instanceData.region,
          instanceId: instanceData.instanceId
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message || 'Failed to query by path');
      }

      return result.data;
    }
  });
};
